import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronDown, X } from "lucide-react";

export default function BookingGuideModal({ open, onClose }) {
  const [openCreateBooking, setOpenCreateBooking] = useState(false);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Booking Guide</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Content (copied from your Guide.jsx Booking section) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Booking</CardTitle>
          </CardHeader>

          <CardContent className="text-sm text-gray-700 space-y-4">
            <ul className="list-disc pl-5 space-y-1">
              <li>Create bookings once details are confirmed.</li>
              <li>Track booking status (approved, pending, cancelled).</li>
              <li>Export PDF when needed.</li>
            </ul>

            {/* Dropdown: How to create a booking */}
            <button
              type="button"
              onClick={() => setOpenCreateBooking((v) => !v)}
              className="w-full flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 hover:bg-gray-50 transition"
            >
              <span className="font-medium text-gray-900">
                How to create a booking
              </span>

              <ChevronDown
                className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
                  openCreateBooking ? "rotate-180" : "rotate-0"
                }`}
              />
            </button>

            {openCreateBooking && (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <ol className="list-decimal pl-5 space-y-2">
                  <li>
                    Go to <span className="font-medium">Booking</span> from the
                    left menu.
                  </li>
                  <li>
                    Click <span className="font-medium">Create Booking</span>.
                  </li>
                  <li>
                    Fill in the booking form:
                    <ul className="list-disc pl-5 mt-2 space-y-1">
                      <li>Property (select the property)</li>
                      <li>Resident / Client name</li>
                      <li>Resident / Client email</li>
                      <li>Resident / Client phone</li>
                      <li>Location</li>
                      <li>Check In (dd/mm/yyyy)</li>
                      <li>Check Out (dd/mm/yyyy)</li>
                      <li>Status (e.g. Pending / Approved / Cancelled)</li>
                    </ul>
                  </li>
                  <li>
                    Click <span className="font-medium">Add</span> to save the
                    booking.
                  </li>
                  <li>
                    Confirm the booking appears in the table and the status is
                    correct.
                  </li>
                </ol>

                <div className="mt-3 text-xs text-gray-500">
                  Tip: Make sure the details are correct to avoid confusion.
                </div>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button
                variant="outline"
                className="rounded-full"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>,
    document.body
  );
}
